import { useState, useEffect, useCallback } from 'react';
import { Loader, Plus, Unlink, Calendar, Clock, Search, ExternalLink, ChevronDown, ChevronRight, Tag, MessageSquare, AlertCircle } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { CalendarEvent, CalendarEventWithRelations } from '../../types/calendar';
import { partnershipsApiService } from '../../services/partnershipsApi';
import { calendarApiService } from '../../services/calendarApi';
import { useDebounce } from '../../hooks/useDebounce';
import { CreatorProfile } from '../CreatorProfile';
import { usernameCacheService } from '../../services/usernameCache';
import { cn } from '../../utils/cn';
import { SectionHeader } from '../ui/SectionHeader';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonLoader } from '../SkeletonLoader';

export interface PartnershipCalendarSectionProps {
  partnershipId: string;
  canEdit: boolean;
  onUpdate: () => void;
}

function fmtTime(ts: number): string {
  const ms = ts > 1e12 ? ts : ts * 1000;
  return format(new Date(ms), 'MMM d, yyyy HH:mm');
}

function fmtRelative(ts: number): string {
  const ms = ts > 1e12 ? ts : ts * 1000;
  return formatDistanceToNow(new Date(ms), { addSuffix: true });
}

const STATUS_STYLES: Record<string, string> = {
  active: 'badge-success',
  cancelled: 'badge-danger',
  postponed: 'badge-amber',
};

export function PartnershipCalendarSection({
  partnershipId,
  canEdit,
  onUpdate,
}: PartnershipCalendarSectionProps) {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'all' | 'upcoming'>('all');

  // Link existing event
  const [showLinkPanel, setShowLinkPanel] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<CalendarEvent[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [linking, setLinking] = useState<string | null>(null);
  const [unlinkingId, setUnlinkingId] = useState<string | null>(null);

  // Create new event
  const [showCreate, setShowCreate] = useState(false);
  const [createTitle, setCreateTitle] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [createStartTime, setCreateStartTime] = useState('');
  const [createEndTime, setCreateEndTime] = useState('');
  const [creating, setCreating] = useState(false);

  // Expanded event detail
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);
  const [expandedDetail, setExpandedDetail] = useState<CalendarEventWithRelations | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = viewMode === 'upcoming'
        ? await partnershipsApiService.getUpcomingCalendarEvents(partnershipId)
        : await partnershipsApiService.getCalendarEvents(partnershipId);
      setEvents(data);
      const uuids = new Set<string>();
      data.forEach(e => { if (e.created_by) uuids.add(e.created_by); });
      if (uuids.size > 0) usernameCacheService.prefetchUsernames(Array.from(uuids));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load calendar events');
    } finally {
      setLoading(false);
    }
  }, [partnershipId, viewMode]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  // Search calendar events for linking
  useEffect(() => {
    if (!showLinkPanel || debouncedSearch.length < 2) {
      setSearchResults([]);
      return;
    }
    let cancelled = false;
    setSearchLoading(true);
    calendarApiService.getAllEvents({ status: 'active' })
      .then((all) => {
        if (cancelled) return;
        const q = debouncedSearch.toLowerCase();
        const linkedIds = new Set(events.map(e => e.id));
        const filtered = all.filter(e =>
          !linkedIds.has(e.id) &&
          (e.title.toLowerCase().includes(q) || e.description?.toLowerCase().includes(q))
        );
        setSearchResults(filtered.slice(0, 20));
      })
      .catch(() => { if (!cancelled) setSearchResults([]); })
      .finally(() => { if (!cancelled) setSearchLoading(false); });
    return () => { cancelled = true; };
  }, [debouncedSearch, showLinkPanel, events]);

  const handleLink = async (eventId: string) => {
    if (!canEdit || linking) return;
    setLinking(eventId);
    try {
      await partnershipsApiService.linkCalendarEvent(partnershipId, eventId);
      await loadEvents();
      onUpdate();
      setSearchResults(prev => prev.filter(e => e.id !== eventId));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to link event');
    } finally {
      setLinking(null);
    }
  };

  const handleUnlink = async (eventId: string) => {
    if (!canEdit || unlinkingId) return;
    if (!window.confirm('Unlink this event from the partnership?')) return;
    setUnlinkingId(eventId);
    try {
      await partnershipsApiService.unlinkCalendarEvent(partnershipId, eventId);
      if (expandedEventId === eventId) { setExpandedEventId(null); setExpandedDetail(null); }
      await loadEvents();
      onUpdate();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to unlink event');
    } finally {
      setUnlinkingId(null);
    }
  };

  const handleCreateAndLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canEdit || creating || !createTitle.trim() || !createStartTime) return;
    setCreating(true);
    try {
      const event = await calendarApiService.createEvent({
        title: createTitle.trim(),
        description: createDescription.trim() || 'Partnership event',
        start_time: new Date(createStartTime).getTime(),
        end_time: createEndTime ? new Date(createEndTime).getTime() : undefined,
        status: 'active',
      });
      await partnershipsApiService.linkCalendarEvent(partnershipId, event.id);
      setCreateTitle('');
      setCreateDescription('');
      setCreateStartTime('');
      setCreateEndTime('');
      setShowCreate(false);
      await loadEvents();
      onUpdate();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create event');
    } finally {
      setCreating(false);
    }
  };

  const handleExpandEvent = async (eventId: string) => {
    if (expandedEventId === eventId) {
      setExpandedEventId(null);
      setExpandedDetail(null);
      return;
    }
    setExpandedEventId(eventId);
    setExpandedDetail(null);
    setDetailLoading(true);
    try {
      const detail = await calendarApiService.getEventById(eventId);
      setExpandedDetail(detail);
      const uuids = new Set<string>();
      if (detail.created_by) uuids.add(detail.created_by);
      detail.users?.forEach(u => uuids.add(u.user_uuid));
      detail.comments?.forEach(c => uuids.add(c.author_id));
      if (uuids.size > 0) usernameCacheService.prefetchUsernames(Array.from(uuids));
    } catch {
      setExpandedDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4" aria-busy="true">
        <div className="flex items-center justify-between">
          <SkeletonLoader className="h-4 w-36" />
          <SkeletonLoader className="h-8 w-40 rounded-lg" />
        </div>
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="subcard px-4 py-3 flex items-center gap-3">
            <SkeletonLoader className="w-4 h-4 rounded" />
            <div className="flex-1 space-y-2">
              <SkeletonLoader className="h-4 w-48" />
              <SkeletonLoader className="h-3 w-64 max-w-full" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        bare
        compact
        tone="danger"
        icon={AlertCircle}
        title="Failed to load calendar events"
        description={error}
        action={<button type="button" onClick={loadEvents} className="glass-button">Retry</button>}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <SectionHeader
        title="Calendar Events"
        icon={Calendar}
        count={events.length}
        className="flex-wrap"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <div className="segmented" role="group" aria-label="Event range">
              <button
                type="button"
                onClick={() => setViewMode('all')}
                className="segmented-item segmented-item-sm"
                data-active={viewMode === 'all' ? 'true' : 'false'}
              >
                All
              </button>
              <button
                type="button"
                onClick={() => setViewMode('upcoming')}
                className="segmented-item segmented-item-sm"
                data-active={viewMode === 'upcoming' ? 'true' : 'false'}
              >
                Upcoming
              </button>
            </div>
            {canEdit && (
              <>
                <button
                  type="button"
                  onClick={() => { setShowLinkPanel(!showLinkPanel); setShowCreate(false); }}
                  className="glass-button-secondary"
                  aria-expanded={showLinkPanel}
                >
                  <Search className="w-4 h-4" />
                  Find Event
                </button>
                <button
                  type="button"
                  onClick={() => { setShowCreate(!showCreate); setShowLinkPanel(false); }}
                  className="glass-button"
                  aria-expanded={showCreate}
                >
                  <Plus className="w-4 h-4" />
                  New Event
                </button>
              </>
            )}
          </div>
        }
      />

      {/* Search & link existing event panel */}
      {showLinkPanel && canEdit && (
        <div className="subcard p-4 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="glass-input w-full pl-9 pr-9"
              placeholder="Search events by title or description…"
              autoFocus
            />
            {searchLoading && <Loader className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-glass-400" />}
          </div>
          {searchQuery.length > 0 && searchQuery.length < 2 && (
            <p className="text-xs text-glass-500 px-1">Type at least 2 characters…</p>
          )}
          {searchResults.length > 0 && (
            <div className="space-y-0.5 max-h-[250px] overflow-y-auto">
              {searchResults.map((event) => (
                <div key={event.id} className="flex items-center justify-between gap-3 px-2 py-2 rounded-md hover:bg-glass-200/10 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm text-white font-medium truncate">{event.title}</span>
                      <span className={cn('badge badge-dot', STATUS_STYLES[event.status])}>{event.status}</span>
                    </div>
                    <span className="text-xs text-glass-500 flex items-center gap-1 mt-0.5 tabular-nums">
                      <Clock className="w-3 h-3" />
                      {fmtTime(event.start_time)}
                      {event.end_time && ` — ${fmtTime(event.end_time)}`}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleLink(event.id)}
                    disabled={linking === event.id}
                    className="glass-button glass-button-sm flex-shrink-0"
                  >
                    {linking === event.id ? <Loader className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                    Link
                  </button>
                </div>
              ))}
            </div>
          )}
          {debouncedSearch.length >= 2 && !searchLoading && searchResults.length === 0 && (
            <p className="text-xs text-glass-500 px-1 py-2">No matching events found</p>
          )}
          <button type="button" onClick={() => { setShowLinkPanel(false); setSearchQuery(''); }} className="glass-button-secondary w-full">Close</button>
        </div>
      )}

      {/* Create new event inline */}
      {showCreate && canEdit && (
        <form onSubmit={handleCreateAndLink} className="subcard p-4 space-y-3">
          <p className="text-xs text-glass-400">Create a new calendar event and link it to this partnership.</p>
          <div>
            <label className="block text-xs font-medium text-glass-400 mb-1.5">Title *</label>
            <input type="text" value={createTitle} onChange={(e) => setCreateTitle(e.target.value)} className="glass-input w-full" placeholder="Event title" required />
          </div>
          <div>
            <label className="block text-xs font-medium text-glass-400 mb-1.5">Description</label>
            <textarea value={createDescription} onChange={(e) => setCreateDescription(e.target.value)} className="glass-input w-full resize-none" rows={2} placeholder="Event description (markdown)" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-glass-400 mb-1.5">Start Time *</label>
              <input type="datetime-local" value={createStartTime} onChange={(e) => setCreateStartTime(e.target.value)} className="glass-input w-full" required />
            </div>
            <div>
              <label className="block text-xs font-medium text-glass-400 mb-1.5">End Time</label>
              <input type="datetime-local" value={createEndTime} onChange={(e) => setCreateEndTime(e.target.value)} className="glass-input w-full" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setShowCreate(false)} className="glass-button-secondary">Cancel</button>
            <button type="submit" disabled={creating || !createTitle.trim() || !createStartTime} className="btn-primary">
              {creating ? <Loader className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Create & Link
            </button>
          </div>
        </form>
      )}

      {/* Event list */}
      <div className="space-y-2">
        {events.length === 0 ? (
          <EmptyState
            bare
            compact
            icon={Calendar}
            title={viewMode === 'upcoming' ? 'No upcoming events linked' : 'No calendar events linked yet'}
            description="Link an existing calendar event or create a new one for this partnership."
          />
        ) : (
          events.map((event) => {
            const isExpanded = expandedEventId === event.id;
            const isPast = event.end_time ? (event.end_time > 1e12 ? event.end_time : event.end_time * 1000) < Date.now()
              : (event.start_time > 1e12 ? event.start_time : event.start_time * 1000) < Date.now();

            return (
              <div key={event.id} className={cn('subcard overflow-hidden', isExpanded && 'border-accent-500/25', isPast && event.status === 'active' && 'opacity-60')}>
                {/* Event row */}
                <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-glass-200/5 transition-colors" onClick={() => handleExpandEvent(event.id)}>
                  {isExpanded ? <ChevronDown className="w-4 h-4 text-glass-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-glass-400 flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-white truncate">{event.title}</span>
                      <span className={cn('badge badge-dot', STATUS_STYLES[event.status])}>{event.status}</span>
                      {event.publish && <span className="chip">public</span>}
                    </div>
                    <div className="flex items-center flex-wrap gap-x-3 gap-y-0.5 mt-0.5 text-xs text-glass-500 tabular-nums">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {fmtTime(event.start_time)}
                        {event.end_time && ` — ${fmtTime(event.end_time)}`}
                      </span>
                      <span>{fmtRelative(event.start_time)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    <a
                      href={`/calendar`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-icon btn-icon-sm"
                      title="Open in Calendar"
                      aria-label="Open in Calendar"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                    {canEdit && (
                      <button
                        type="button"
                        onClick={() => handleUnlink(event.id)}
                        disabled={unlinkingId === event.id}
                        className={cn('btn-icon btn-icon-sm text-red-400 hover:text-red-300', unlinkingId === event.id && 'opacity-50')}
                        title="Unlink event"
                        aria-label="Unlink event"
                      >
                        {unlinkingId === event.id ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Unlink className="w-3.5 h-3.5" />}
                      </button>
                    )}
                  </div>
                </div>

                {/* Expanded detail */}
                {isExpanded && (
                  <div className="px-4 pb-4 pt-3 border-t border-glass-200/10 space-y-3">
                    {detailLoading ? (
                      <div className="space-y-2 py-1" aria-busy="true">
                        <SkeletonLoader className="h-3 w-24" />
                        <SkeletonLoader className="h-3 w-full" />
                        <SkeletonLoader className="h-3 w-2/3" />
                      </div>
                    ) : expandedDetail ? (
                      <>
                        {/* Description */}
                        {expandedDetail.description && (
                          <div>
                            <p className="eyebrow mb-1">Description</p>
                            <p className="text-sm text-glass-200 whitespace-pre-wrap">{expandedDetail.description}</p>
                          </div>
                        )}

                        {/* Tags */}
                        {expandedDetail.tags && expandedDetail.tags.length > 0 && (
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <Tag className="w-3 h-3 text-glass-500" />
                            {expandedDetail.tags.map((tag) => (
                              <span key={tag.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border" style={{ backgroundColor: `${tag.color}20`, borderColor: `${tag.color}40`, color: tag.color }}>
                                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: tag.color }} />
                                {tag.name}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Assigned users */}
                        {expandedDetail.users && expandedDetail.users.length > 0 && (
                          <div>
                            <p className="eyebrow mb-1">Assigned ({expandedDetail.users.length})</p>
                            <div className="flex flex-wrap gap-2">
                              {expandedDetail.users.map((u) => (
                                <CreatorProfile key={u.user_uuid} creatorId={u.user_uuid} inline />
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Comments */}
                        {expandedDetail.comments && expandedDetail.comments.length > 0 && (
                          <div>
                            <p className="eyebrow mb-1.5 flex items-center gap-1">
                              <MessageSquare className="w-3 h-3" />
                              Comments ({expandedDetail.comments.length})
                            </p>
                            <div className="space-y-2 max-h-[200px] overflow-y-auto">
                              {expandedDetail.comments.map((comment) => (
                                <div key={comment.id} className="text-xs rounded-md bg-dark-900/30 border border-glass-200/10 p-2">
                                  <div className="flex items-center gap-2 mb-1">
                                    <CreatorProfile creatorId={comment.author_id} inline />
                                    <span className="text-glass-500 tabular-nums">{fmtRelative(comment.created_at)}</span>
                                  </div>
                                  <p className="text-glass-300 whitespace-pre-wrap">{comment.content}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Metadata */}
                        <div className="text-[11px] text-glass-500 pt-2 border-t border-glass-200/10 flex items-center flex-wrap gap-x-3 gap-y-1 tabular-nums">
                          <span>Created {fmtRelative(expandedDetail.created_at)} by <CreatorProfile creatorId={expandedDetail.created_by} inline /></span>
                          {expandedDetail.updated_at && <span>Updated {fmtRelative(expandedDetail.updated_at)}</span>}
                          <span className="font-mono">{expandedDetail.id.slice(0, 12)}…</span>
                        </div>
                      </>
                    ) : (
                      <p className="text-sm text-glass-500">Failed to load event details</p>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
